import {
  withFetch,
  HttpClient,
  withInterceptors,
  provideHttpClient,
} from "@angular/common/http";
import {
  enableProdMode,
  APP_INITIALIZER,
  importProvidersFrom,
  isDevMode,
} from "@angular/core";
import {
  getAuth,
  provideAuth,
  initializeAuth,
  indexedDBLocalPersistence,
} from "@angular/fire/auth";
import {
  getFirestore,
  provideFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "@angular/fire/firestore";
import {
  provideRouter,
  withPreloading,
  PreloadAllModules,
  RouteReuseStrategy,
  withComponentInputBinding,
} from "@angular/router";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from "@ionic/angular/standalone";
import {
  provideLottieOptions,
  provideCacheableAnimationLoader,
} from "ngx-lottie";
import {
  getRemoteConfig,
  provideRemoteConfig,
} from "@angular/fire/remote-config";
import player from "lottie-web";
import { Drivers } from "@ionic/storage";
import { routes } from "./app/app.routes";
import { provideStore } from "@ngrx/store";
import { Capacitor } from "@capacitor/core";
import { Reducer } from "./app/store/reducer";
import { AppComponent } from "./app/app.component";
import { provideHotToastConfig } from "@ngxpert/hot-toast";
import { IonicStorageModule } from "@ionic/storage-angular";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { bootstrapApplication } from "@angular/platform-browser";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { defineCustomElements } from "@ionic/pwa-elements/loader";
import { getStorage, provideStorage } from "@angular/fire/storage";
import { getDatabase, provideDatabase } from "@angular/fire/database";
import { getFunctions, provideFunctions } from "@angular/fire/functions";
import { environment, firebaseConfig } from "./environments/environment";
import { getApp, initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { StorageService } from "./app/services/storage-service/storage.service";
import { LanguageService } from "./app/services/language-service/language.service";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { AuthInterceptor } from "./app/interceptors/auth-interceptor/auth.interceptor";

defineCustomElements(window);

if (environment.production) enableProdMode();

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

export function initializeLanguage(
  languageService: LanguageService,
  storageService: StorageService,
) {
  return async () => {
    const langCode = await storageService.get("selectedLanguage");
    await languageService.setLanguage(langCode || "en");
  };
}

export const firebaseProviders = [
  // 🔹 Firebase App
  provideFirebaseApp(() => initializeApp(firebaseConfig)),

  // 🔹 Firebase Auth
  provideAuth(() => {
    const app = getApp();
    if (Capacitor.isNativePlatform()) {
      return initializeAuth(app, {
        persistence: indexedDBLocalPersistence,
      });
    }
    return getAuth(app);
  }),

  // 🔹 Firestore (with safe persistence fallback)
  provideFirestore(() => {
    const app = getApp();

    try {
      return initializeFirestore(app, {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
      });
    } catch (err) {
      console.warn("Firestore persistence unavailable, falling back", err);
      return getFirestore(app);
    }
  }),

  // 🔹 Storage
  provideStorage(() => {
    const app = getApp();
    return getStorage(app);
  }),

  // 🔹 Realtime Database
  provideDatabase(() => {
    const app = getApp();
    return getDatabase(app);
  }),

  // 🔹 Cloud Functions
  provideFunctions(() => {
    const app = getApp();
    return getFunctions(app);
  }),

  // 🔹 Remote Config
  provideRemoteConfig(() => {
    const remoteConfig = getRemoteConfig(getApp());

    remoteConfig.settings = {
      fetchTimeoutMillis: 10000,
      minimumFetchIntervalMillis: isDevMode() ? 60000 : 3600000,
    };

    return remoteConfig;
  }),
];

bootstrapApplication(AppComponent, {
  providers: [
    // 🔹 Firebase Providers
    ...firebaseProviders,

    // 🔹 Toast Notifications
    provideHotToastConfig({
      visibleToasts: 5,
      stacking: "depth",
      dismissible: true,
      reverseOrder: true,
    }),

    // 🔹 Ionic Framework
    provideIonicAngular({
      mode: "md",
      swipeBackEnabled: false,
      hardwareBackButton: false,
    }),

    // 🔹 Router Configuration
    provideRouter(
      routes,
      withComponentInputBinding(),
      withPreloading(PreloadAllModules),
    ),

    // 🔹 Translation & IndexedDB Storage
    importProvidersFrom(
      IonicStorageModule.forRoot({
        storeName: "bog_pro",
        driverOrder: [Drivers.IndexedDB],
      }),
      TranslateModule.forRoot({
        loader: {
          deps: [HttpClient],
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
        },
      }),
    ),

    // 🔹 Language Initializer
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      deps: [LanguageService, StorageService],
      multi: true,
    },

    // 🔹 NgRx Store
    provideStore(Reducer),
    ...(isDevMode()
      ? [provideStoreDevtools({ maxAge: 30, logOnly: true })]
      : []),

    // 🔹 Animations & Lottie
    provideAnimationsAsync(),
    provideCacheableAnimationLoader(),
    provideLottieOptions({ player: () => player }),

    // 🔹 HTTP Client & Interceptors
    provideHttpClient(withFetch(), withInterceptors([AuthInterceptor])),

    // 🔹 Ionic Route Reuse Strategy
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
  ],
});

// bootstrapApplication(AppComponent, {
//   providers: [
//     provideQuillConfig({}),
//     provideHotToastConfig({
//       visibleToasts: 5,
//       stacking: "depth",
//       dismissible: true,
//       reverseOrder: true,
//     }),
//     provideIonicAngular({
//       mode: "md",
//       swipeBackEnabled: false,
//       hardwareBackButton: false,
//     }),
//     provideRouter(
//       routes,
//       withComponentInputBinding(),
//       withPreloading(PreloadAllModules),
//     ),
//     importProvidersFrom(
//       TranslateModule.forRoot({
//         loader: {
//           deps: [HttpClient],
//           provide: TranslateLoader,
//           useFactory: createTranslateLoader,
//         },
//       }),
//     ),
//     {
//       multi: true,
//       provide: APP_INITIALIZER,
//       deps: [LanguageService],
//       useFactory: (langService: LanguageService) => () =>
//         langService.initLanguage(),
//     },
//     provideAuth(() => {
//       if (Capacitor.isNativePlatform()) {
//         return initializeAuth(getApp(), {
//           persistence: indexedDBLocalPersistence,
//         });
//       } else return getAuth();
//     }),
//     provideFirestore(() => {
//       return initializeFirestore(getApp(), {
//         localCache: persistentLocalCache({
//           tabManager: persistentMultipleTabManager(),
//         }),
//       });
//     }),
//     provideStore(Reducer),
//     provideAnimationsAsync(),
//     provideCacheableAnimationLoader(),
//     provideStorage(() => getStorage()),
//     provideDatabase(() => getDatabase()),
//     provideStoreDevtools({ maxAge: 30 }),
//     provideFunctions(() => getFunctions()),
//     provideRemoteConfig(() => {
//       const remoteConfig = getRemoteConfig();
//       remoteConfig.settings = {
//         fetchTimeoutMillis: 10000,
//         minimumFetchIntervalMillis: isDevMode() ? 10000 : 3600000,
//       };
//       return remoteConfig;
//     }),
//     provideLottieOptions({ player: () => player }),
//     provideFirebaseApp(() => initializeApp(firebaseConfig)),
//     { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
//     provideHttpClient(withFetch(), withInterceptors([AuthInterceptor])),
//     importProvidersFrom(IonicStorageModule.forRoot({ storeName: "bog_pro" })),
//   ],
// });

// import {
//   getAnalytics,
//   provideAnalytics,
//   ScreenTrackingService,
//   UserTrackingService,
// } from "@angular/fire/analytics";
// import {
//   initializeAppCheck,
//   ReCaptchaEnterpriseProvider,
//   provideAppCheck,
// } from "@angular/fire/app-check";
// import { getStorage, provideStorage } from "@angular/fire/storage";
// import { getDatabase, provideDatabase } from "@angular/fire/database";
// import { getFunctions, provideFunctions } from "@angular/fire/functions";
// import { getMessaging, provideMessaging } from "@angular/fire/messaging";

// provideStorage(() => getStorage()),
// provideAnalytics(() => getAnalytics()),
// provideMessaging(() => getMessaging()),
